/*
 * AI Generated Corrective Action Plan — paid upgrade page.
 *
 * Routed from the AI Plan upgrade card on the Caporilli diagnostic dashboard.
 * Shows product details, $1,000 price, and three payment options:
 *   1. Credit Card  (MOCKED — auto-marks paid and unlocks the entitlement)
 *   2. ACH          (MOCKED — submits as pending_payment, Super Admin reconciles)
 *   3. Purchase Order (submits as pending_po, Super Admin approves)
 *
 * The screen reuses the Executive Clarity design language. No card details are
 * collected — the CC option is a test-mode trigger only, replaced with real
 * Stripe wiring when payment provider is enabled.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { ArrowLeft, CreditCard, FileText, Building2, Sparkles, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const getSession = () => { try { return JSON.parse(localStorage.getItem("diag_session") || "null"); } catch { return null; } };
const getHdrs = () => {
  const s = getSession();
  if (!s?.user?.id || !s?.tenant?.id) return null;
  return { "x-tenant-id": s.tenant.id, "x-user-id": s.user.id, "x-user-role": s.user.role || "tenant_admin" };
};

const AIPlanUpgradePage = () => {
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState("credit_card");
  const [poNumber, setPoNumber] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const h = getHdrs();
    if (!h) {
      navigate("/diagnostic/login");
      return;
    }
    axios.get(`${API}/api/license/caporilli_diagnostic/ai-plan-upgrade/quote`, { headers: h })
      .then((r) => setQuote(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load upgrade details"))
      .finally(() => setLoading(false));
  }, [navigate]);

  const submit = async () => {
    if (!method) { toast.error("Select a payment method"); return; }
    if (method === "po" && !poNumber.trim()) {
      toast.error("Please enter your Purchase Order number");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/api/license/caporilli_diagnostic/ai-plan-upgrade`,
        {
          payment_method: method,
          po_number: poNumber.trim(),
          contact_name: contactName.trim(),
          contact_email: contactEmail.trim(),
          note: note.trim(),
        },
        { headers: getHdrs() }
      );
      setResult(r.data);
      if (r.data.ai_corrective_action_plan_enabled) {
        toast.success("Upgrade activated! AI Generated Plan is now unlocked.");
      } else {
        toast.success("Upgrade request submitted. We'll confirm shortly.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upgrade failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !quote) {
    return (
      <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center px-4">
        <div className="text-[#6b7280]">Loading upgrade details…</div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-[#f9fafb] px-4 py-12" data-testid="ai-plan-upgrade-success">
        <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-[#e5e7eb] p-8 sm:p-10 text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#ecfdf5] mb-4">
            <CheckCircle2 className="h-7 w-7 text-[#059669]" />
          </div>
          <h1 className="text-2xl font-bold text-[#111827]">
            {result.ai_corrective_action_plan_enabled ? "Upgrade Activated" : "Request Submitted"}
          </h1>
          <p className="mt-3 text-[#4b5563] text-[15px]">{result.message}</p>
          <p className="mt-4 text-[12px] text-[#6b7280]">
            Reference: <strong className="text-[#374151]">{result.payment_id}</strong> · Status: <strong className="text-[#374151]">{result.status}</strong>
          </p>
          <Button onClick={() => navigate("/diagnostic")} className="mt-6 bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="ai-plan-upgrade-back">
            Back to Diagnostic Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f9fafb]" data-testid="ai-plan-upgrade-page">
      <header className="sticky top-0 z-30 bg-white border-b border-[#e5e7eb]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 flex items-center justify-between h-20">
          <button onClick={() => navigate("/diagnostic")} className="flex items-center shrink-0" data-testid="ai-plan-upgrade-logo">
            <img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="h-10 w-auto" />
          </button>
          <button
            onClick={() => navigate("/diagnostic")}
            className="text-[13px] text-[#6b7280] hover:text-[#111827] inline-flex items-center gap-1.5"
            data-testid="ai-plan-upgrade-cancel"
          >
            <ArrowLeft className="h-4 w-4" /> Cancel
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-5 gap-8 overflow-x-hidden">
        {/* Left — product detail */}
        <div className="lg:col-span-3 min-w-0">
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#00698a] font-semibold">Paid Upgrade</span>
          <h1 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold text-[#111827] leading-tight break-words">{quote.product_name}</h1>
          <p className="mt-3 text-[15px] text-[#4b5563]">{quote.description}</p>

          <div className="mt-6 rounded-xl border border-[#e5e7eb] bg-white p-5 space-y-3">
            <div className="flex items-center gap-2 text-[13px] text-[#111827]">
              <Sparkles className="h-4 w-4 text-[#00698a]" />
              <strong>What you get</strong>
            </div>
            <ul className="text-[13px] text-[#4b5563] space-y-1.5 list-disc pl-5">
              <li>AI-assisted improvement priorities from your weakest pillars</li>
              <li>Recommended actions, suggested owners, and 12-week task plan</li>
              <li>Board-ready corrective action narrative</li>
              <li>One-click regenerate whenever your diagnostic is updated</li>
            </ul>
          </div>

          <div className="mt-6 rounded-lg border border-[#bfdbfe] bg-[#eff6ff] px-4 py-3 text-[12px] text-[#1e3a8a]">
            <strong>Add-on to your annual license.</strong> This upgrade is in addition to your existing
            <span className="ml-1 font-semibold">{quote.is_add_on_to}</span> and does not change your annual renewal pricing.
          </div>
        </div>

        {/* Right — payment summary + method picker */}
        <div className="lg:col-span-2 min-w-0">
          <div className="rounded-xl border border-[#e5e7eb] bg-white p-4 sm:p-5 lg:sticky lg:top-24">
            <div className="text-[11px] uppercase tracking-wider text-[#6b7280] font-semibold">Price</div>
            <div className="mt-1 flex items-baseline flex-wrap gap-1.5" data-testid="ai-plan-upgrade-price">
              <div className="text-3xl sm:text-4xl font-bold text-[#111827]">${quote.price.toLocaleString()}</div>
              <div className="text-[13px] text-[#6b7280]">{quote.currency} · one-time</div>
            </div>

            <div className="mt-5 pt-4 border-t border-[#e5e7eb] space-y-2">
              <div className="flex items-start gap-2 text-[13px]">
                <Building2 className="h-4 w-4 text-[#6b7280] mt-0.5 shrink-0" />
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#6b7280] font-semibold">School</div>
                  <div className="text-[#111827] font-semibold" data-testid="ai-plan-upgrade-school">{quote.school_name || "—"}</div>
                </div>
              </div>
              <div className="text-[11px] text-[#6b7280]">
                License: <span className="text-[#374151]">{quote.license_status}</span>
              </div>
            </div>

            <div className="mt-5 pt-4 border-t border-[#e5e7eb]">
              <div className="text-[11px] uppercase tracking-wider text-[#6b7280] font-semibold mb-2">Payment Method</div>
              <div className="space-y-2" data-testid="ai-plan-upgrade-method-picker">
                {[
                  { id: "credit_card", label: "Credit Card", icon: CreditCard, hint: "Instant unlock (test mode)" },
                  { id: "ach", label: "ACH Transfer", icon: Building2, hint: "Unlock after we confirm payment" },
                  { id: "po", label: "Purchase Order", icon: FileText, hint: "Unlock after Super Admin approval" },
                ].map((opt) => {
                  const Icon = opt.icon;
                  const selected = method === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setMethod(opt.id)}
                      className={`w-full text-left rounded-lg border p-3 flex items-center gap-3 transition-colors ${
                        selected ? "border-[#007a9e] bg-[#f0f9fc]" : "border-[#e5e7eb] hover:border-[#007a9e]/40"
                      }`}
                      data-testid={`ai-plan-upgrade-method-${opt.id}`}
                    >
                      <Icon className={`h-4 w-4 ${selected ? "text-[#00698a]" : "text-[#6b7280]"}`} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-[13px] font-semibold ${selected ? "text-[#00485f]" : "text-[#111827]"}`}>{opt.label}</div>
                        <div className="text-[11px] text-[#6b7280]">{opt.hint}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {method === "po" && (
              <div className="mt-3 space-y-2" data-testid="ai-plan-upgrade-po-fields">
                <Input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="PO # *" className="bg-white border-[#e5e7eb] h-9 text-[13px]" data-testid="ai-plan-upgrade-po-number" />
              </div>
            )}

            <div className="mt-3 space-y-2">
              <Input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Contact name (optional)" className="bg-white border-[#e5e7eb] h-9 text-[13px]" data-testid="ai-plan-upgrade-contact-name" />
              <Input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="Contact email (optional)" className="bg-white border-[#e5e7eb] h-9 text-[13px]" data-testid="ai-plan-upgrade-contact-email" />
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="bg-white border-[#e5e7eb] text-[13px] min-h-[60px]" data-testid="ai-plan-upgrade-note" />
            </div>

            <Button
              onClick={submit}
              disabled={submitting || quote.already_enabled}
              className="mt-4 w-full bg-[#007a9e] hover:bg-[#00607d] text-white disabled:opacity-60"
              data-testid="ai-plan-upgrade-submit"
            >
              {quote.already_enabled
                ? "Already Active"
                : submitting
                  ? "Submitting…"
                  : method === "credit_card"
                    ? "Pay & Unlock — $1,000"
                    : method === "ach"
                      ? "Submit ACH Request — $1,000"
                      : "Submit PO Request — $1,000"}
            </Button>
            <p className="mt-3 text-[10px] text-[#9ca3af] text-center">
              Payment is processed manually while Stripe wiring is finalized. No card details are stored.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIPlanUpgradePage;
